import React, { useMemo } from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { orderBy } from 'lodash'
import { TABLE_DIRECTION_DESC, TABLE_WIDTH_DOT_CHOICE } from 'lib/tableLib'
import {
  CAR_CHOICE_ACCEPTED,
  CAR_CHOICE_ACCEPTED_WITH_REPRICE,
} from 'lib/carChoicesLib'

import Table from 'components/atoms/Table'
import DotCell from 'components/atoms/DotCell'
import TableHeaderCell from 'components/atoms/TableHeaderCell'

import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useSort from 'hooks/useSort'

import { getAssignmentState } from '../Assignment/assignmentLib'

import AssignToBar from './AssignToBar'
import AssignToDialog from './AssignToDialog'

const createColumns = (t, session) => {
  return [
    {
      accessor: 'partnerName',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.partner')} />
      ),
    },
    {
      accessor: 'originalPrice',
      Header: (
        <TableHeaderCell
          label={t('assignment.car-details.table.originalPrice')}
        />
      ),
    },
    {
      accessor: 'acceptedChoice',
      Header: (
        <TableHeaderCell
          label={t(`assignment.car-details.table.${CAR_CHOICE_ACCEPTED}`)}
        />
      ),
      Cell: ({ row }) =>
        row.original.acceptedChoice && <DotCell color="primary" />,
      cellProps: {
        sx: {
          width: TABLE_WIDTH_DOT_CHOICE,
        },
      },
    },
    {
      accessor: 'acceptedWithRepriceChoice',
      Header: (
        <TableHeaderCell
          label={t('assignment.car-details.table.acceptedWithReprice')}
        />
      ),
      Cell: ({ row }) =>
        row.original.acceptedWithRepriceChoice && <DotCell color="primary" />,
      cellProps: {
        sx: {
          width: TABLE_WIDTH_DOT_CHOICE,
        },
      },
    },
    {
      accessor: 'reprice',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.reprice')} />
      ),
    },
    {
      accessor: 'assigned',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.assigned')} />
      ),
      Cell: ({ value }) =>
        !!value && (
          <DotCell
            // label={t(
            //   `assignment.status.${session.agenda.assignment.state}.${value}`
            // )}
            color="primary"
          />
        ),
    },
    {
      accessor: 'price',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.price')} />
      ),
    },
  ]
}

export default function AssignmentCarTable({
  session,
  carChoice,
  handleAssignTo,
  isUpdating,
  readOnly,
}) {
  const { t } = useTranslation()

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleAssignTo)

  const { order, setSort } = useSort('status', TABLE_DIRECTION_DESC)

  const car = useMemo(
    () => carChoice.assignments.find((car) => car.vin === carChoice.car.vin),
    [carChoice]
  )

  const partners = useMemo(
    () => carChoice?.choices.filter((partner) => partner.groupId),
    [carChoice?.choices]
  )

  const partnersInTable = useMemo(
    () =>
      partners
        ?.map((partner) =>
          (!partner.choice || !partner.groupId) &&
          car?.assignedPartnerId !== partner.partnerId
            ? null
            : partner
        )
        .filter((partner) => partner),
    [car?.assignedPartnerId, partners]
  )

  const columns = useMemo(() => createColumns(t, session), [t, session])

  const preparedItems = useMemo(() => {
    const numberFormatter = new Intl.NumberFormat('it-IT')

    return partnersInTable.map((partner) => {
      const originalPrice =
        carChoice.car[`price_group_${partner.groupId}`] + carChoice.car.fee

      const assigned =
        car?.assignedPartnerId === partner.partnerId
          ? getAssignmentState(car)
          : ''

      return {
        partnerName: partner.partnerName,
        originalPrice: originalPrice
          ? numberFormatter.format(originalPrice)
          : '-',
        acceptedChoice: partner.choice === CAR_CHOICE_ACCEPTED,
        acceptedWithRepriceChoice:
          partner.choice === CAR_CHOICE_ACCEPTED_WITH_REPRICE,
        reprice: partner.reprice
          ? numberFormatter.format(partner.reprice)
          : '-',
        assigned,
        price: assigned
          ? numberFormatter.format(
              car.finalPrice + (car.assignmentFee || car.fee)
            )
          : '-',
        assignedOrder: assigned ? Number.MAX_SAFE_INTEGER : partner.cost,
      }
    })
  }, [car, carChoice.car, partnersInTable])

  const sortedPartners = useMemo(
    () =>
      orderBy(
        preparedItems,
        [order?.column !== 'assigned' ? order?.column : 'assignedOrder'],
        [order?.direction]
      ),
    [preparedItems, order?.column, order?.direction]
  )

  return (
    <>
      <Table
        items={sortedPartners || []}
        columns={columns}
        isFetching={isUpdating}
        sortable
        orderColumn={order?.column}
        orderDirection={order?.direction}
        handleSort={setSort}
        bottom={
          <AssignToBar
            handleAssignTo={handleOpenConfirmationDialog}
            readOnly={readOnly}
          />
        }
      />
      <AssignToDialog
        partners={partners}
        carChoice={carChoice}
        openConfirmationDialog={openConfirmationDialog}
        handleCloseConfirmationDialog={handleCloseConfirmationDialog}
        handleConfirmConfirmationDialog={handleConfirmConfirmationDialog}
      />
    </>
  )
}

AssignmentCarTable.propTypes = {
  session: T.object.isRequired,
  carChoice: T.object,
  handleAssignTo: T.func,
  isUpdating: T.bool,
  readOnly: T.bool,
}
